/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { useState, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  BookOpen,
  Library,
  HardHat,
  Settings as SettingsIcon,
  FileText,
  Wrench,
  Server,
  Database,
  Sparkles,
} from 'lucide-react';
import type { PlaybooksSettings } from '@shared/types/requests/user-settings';
import type { UISettings } from '@shared/types/user_settings';

// Import tab components
import { PlaybooksTab } from '@/components/playbooks/context-library/PlaybooksTab';
import { SkillsTab } from '@/components/playbooks/context-library/SkillsTab';
import { McpServersTab } from '@/components/playbooks/context-library/McpServersTab';
import { GeneratePlaybookTab } from '@/components/playbooks/knowledge-mining/GeneratePlaybookTab';
import { MiningJobsTab } from '@/components/playbooks/knowledge-mining/MiningJobsTab';
import { SourcesTab } from '@/components/playbooks/knowledge-mining/SourcesTab';
import { SettingsTab } from '@/components/playbooks/settings/SettingsTab';
import { ActiveProductIndicator } from '@/components/common/ActiveProductIndicator';

// Services - Using query function factories for simple endpoints
import {
  // Query keys for cache management
  PLAYBOOK_QUERY_KEYS,
  SKILLS_QUERY_KEYS,
  MCP_QUERY_KEYS,
  // Query function factories
  playbookIndexQueryFn,
  skillsIndexQueryFn,
  mcpStatsQueryFn,
  // User settings
  getUserSettings,
  updatePlaybooksSettings,
} from '@/services/skillshop';

type MainTab = 'context-library' | 'knowledge-mining' | 'settings';
type ContextLibrarySubTab = 'playbooks' | 'skills' | 'mcp-servers';
type KnowledgeMiningSubTab = 'generate' | 'jobs' | 'sources';

export function Playbooks() {
  const [activeTab, setActiveTab] = useState<MainTab>('context-library');
  const [contextLibrarySubTab, setContextLibrarySubTab] = useState<ContextLibrarySubTab>('playbooks');
  const [knowledgeMiningSubTab, setKnowledgeMiningSubTab] = useState<KnowledgeMiningSubTab>('generate');
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  // Fetch user settings to restore saved preferences
  const { data: userSettings } = useQuery({
    queryKey: ['user-settings'],
    queryFn: getUserSettings,
    staleTime: 5 * 60 * 1000,
  });

  // Initialize tabs from saved settings once loaded
  useEffect(() => {
    if (userSettings && !settingsLoaded) {
      const playbooksSettings = (userSettings.uiSettings as UISettings)?.playbooksSettings;
      if (playbooksSettings) {
        if (playbooksSettings.defaultMainTab) {
          setActiveTab(playbooksSettings.defaultMainTab);
        }
        if (playbooksSettings.defaultContextLibrarySubTab) {
          setContextLibrarySubTab(playbooksSettings.defaultContextLibrarySubTab);
        }
        if (playbooksSettings.defaultKnowledgeMiningSubTab) {
          setKnowledgeMiningSubTab(playbooksSettings.defaultKnowledgeMiningSubTab);
        }
      }
      setSettingsLoaded(true);
    }
  }, [userSettings, settingsLoaded]);

  // Save tab preferences when they change
  const handleMainTabChange = useCallback((tab: MainTab) => {
    setActiveTab(tab);
    // Save to user settings (fire and forget)
    updatePlaybooksSettings({ defaultMainTab: tab });
  }, []);

  const handleContextLibrarySubTabChange = useCallback((tab: ContextLibrarySubTab) => {
    setContextLibrarySubTab(tab);
    updatePlaybooksSettings({ defaultContextLibrarySubTab: tab });
  }, []);

  const handleKnowledgeMiningSubTabChange = useCallback((tab: KnowledgeMiningSubTab) => {
    setKnowledgeMiningSubTab(tab);
    updatePlaybooksSettings({ defaultKnowledgeMiningSubTab: tab });
  }, []);

  // Fetch summary data for badges - using query function factories
  // Data auto-refreshes on window focus, component mount, and after mutations
  const { data: playbookIndex } = useQuery({
    queryKey: PLAYBOOK_QUERY_KEYS.index,
    queryFn: playbookIndexQueryFn,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const { data: skillsIndex } = useQuery({
    queryKey: SKILLS_QUERY_KEYS.index,
    queryFn: skillsIndexQueryFn,
    staleTime: 5 * 60 * 1000,
  });

  const { data: mcpStats } = useQuery({
    queryKey: MCP_QUERY_KEYS.stats,
    queryFn: mcpStatsQueryFn,
    staleTime: 5 * 60 * 1000,
  });

  const playbooksCount = playbookIndex?.stats?.totalPlaybooks ?? 0;
  const skillsCount = skillsIndex?.stats?.totalSkills ?? 0;
  const mcpServersCount = mcpStats?.totalServers ?? 0;

  // Tab button style helper
  const getTabStyle = (tab: MainTab) => ({
    padding: '12px 24px',
    fontSize: '14px',
    fontWeight: 500,
    border: 'none',
    background: 'transparent',
    borderBottom: activeTab === tab ? '2px solid #0066cc' : '2px solid transparent',
    marginBottom: '-2px',
    color: activeTab === tab ? '#0066cc' : '#6b7280',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.2s ease',
  });

  // Sub-tab button style helper
  const getSubTabStyle = (isActive: boolean) => ({
    padding: '8px 16px',
    fontSize: '13px',
    fontWeight: 500,
    border: 'none',
    background: isActive ? '#e6f0ff' : 'transparent',
    borderRadius: '6px',
    color: isActive ? '#0066cc' : '#6b7280',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    transition: 'all 0.2s ease',
  });

  // Badge style helper
  const getBadgeStyle = (variant: 'default' | 'warning' = 'default') => ({
    backgroundColor: variant === 'warning' ? '#fbbf24' : '#e5e7eb',
    color: variant === 'warning' ? '#92400e' : '#374151',
    fontSize: '11px',
    fontWeight: 600,
    padding: '2px 8px',
    borderRadius: '9999px',
    minWidth: '20px',
    textAlign: 'center' as const,
  });

  return (
    <div className="container py-6">
      <div className="card">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3">
              <BookOpen className="h-6 w-6 text-primary" />
              <div>
                <h1 className="text-2xl font-semibold">Playbooks</h1>
                <p className="text-sm text-muted-foreground">
                  Context Library for AI agents
                </p>
              </div>
            </div>
            <div className="h-8 w-px bg-border" />
            <ActiveProductIndicator />
          </div>
        </div>

        {/* Main Tabs */}
        <div style={{
          display: 'flex',
          borderBottom: '2px solid #e5e7eb',
          marginBottom: '24px',
          gap: '4px',
        }}>
          <button onClick={() => handleMainTabChange('context-library')} style={getTabStyle('context-library')}>
            <Library size={16} />
            Context Library
            <span style={getBadgeStyle()}>{playbooksCount + skillsCount + mcpServersCount}</span>
          </button>
          <button onClick={() => handleMainTabChange('knowledge-mining')} style={getTabStyle('knowledge-mining')}>
            <HardHat size={16} />
            Knowledge Mining
          </button>
          <button onClick={() => handleMainTabChange('settings')} style={getTabStyle('settings')}>
            <SettingsIcon size={16} />
            Settings
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'context-library' && (
          <>
            {/* Context Library Sub-Tabs */}
            <div style={{
              display: 'flex',
              gap: '8px',
              marginBottom: '20px',
              padding: '4px',
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              width: 'fit-content',
            }}>
              <button
                onClick={() => handleContextLibrarySubTabChange('playbooks')}
                style={getSubTabStyle(contextLibrarySubTab === 'playbooks')}
              >
                <FileText size={14} />
                Playbooks
                <span style={getBadgeStyle()}>{playbooksCount}</span>
              </button>
              <button
                onClick={() => handleContextLibrarySubTabChange('skills')}
                style={getSubTabStyle(contextLibrarySubTab === 'skills')}
              >
                <Wrench size={14} />
                Skills
                <span style={getBadgeStyle()}>{skillsCount}</span>
              </button>
              <button
                onClick={() => handleContextLibrarySubTabChange('mcp-servers')}
                style={getSubTabStyle(contextLibrarySubTab === 'mcp-servers')}
              >
                <Server size={14} />
                MCP Servers
                <span style={getBadgeStyle()}>{mcpServersCount}</span>
              </button>
            </div>

            {/* Sub-Tab Content */}
            {contextLibrarySubTab === 'playbooks' && <PlaybooksTab />}
            {contextLibrarySubTab === 'skills' && <SkillsTab />}
            {contextLibrarySubTab === 'mcp-servers' && <McpServersTab />}
          </>
        )}

        {activeTab === 'knowledge-mining' && (
          <>
            {/* Knowledge Mining Sub-Tabs */}
            <div style={{
              display: 'flex',
              gap: '8px',
              marginBottom: '20px',
              padding: '4px',
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              width: 'fit-content',
            }}>
              <button
                onClick={() => handleKnowledgeMiningSubTabChange('generate')}
                style={getSubTabStyle(knowledgeMiningSubTab === 'generate')}
              >
                <Sparkles size={14} />
                Generate
              </button>
              <button
                onClick={() => handleKnowledgeMiningSubTabChange('jobs')}
                style={getSubTabStyle(knowledgeMiningSubTab === 'jobs')}
              >
                <HardHat size={14} />
                Mining Jobs
              </button>
              <button
                onClick={() => handleKnowledgeMiningSubTabChange('sources')}
                style={getSubTabStyle(knowledgeMiningSubTab === 'sources')}
              >
                <Database size={14} />
                Sources
              </button>
            </div>

            {/* Sub-Tab Content */}
            {knowledgeMiningSubTab === 'generate' && <GeneratePlaybookTab />}
            {knowledgeMiningSubTab === 'jobs' && <MiningJobsTab />}
            {knowledgeMiningSubTab === 'sources' && <SourcesTab />}
          </>
        )}

        {activeTab === 'settings' && <SettingsTab />}
      </div>
    </div>
  );
}
